import { createSelector, createSlice, isAnyOf } from '@reduxjs/toolkit';

import { authApi } from '~services/api/auth';
import {
	loginMatchers,
	registerMatchers,
	registerSSOMatchers,
} from '~services/api/auth/matchers';
import type { TAuthUserInfo } from '~services/api/auth/types';
import { userApi } from '~services/api/user';
import { TokenManager } from '~shared/services/ClientTokenManager';

import type { RootState } from '../index';

type AuthState = {
	info: TAuthUserInfo;
};
const initialState: AuthState = {
	info: {
		id: '',
		email: '',
		roles: [],
		isSafe: false,
		nick: '',
		registrationDate: '',
	},
};
const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addMatcher(
				isAnyOf(
					authApi.endpoints.register.matchFulfilled,
					authApi.endpoints.changeEmail.matchFulfilled
				),
				(state, action) => {
					state.info.email = action.meta.arg.originalArgs.email;
				}
			)
			.addMatcher(isAnyOf(...registerSSOMatchers), (state, action) => {
				state.info.email = action.payload.email;
			})
			.addMatcher(
				isAnyOf(userApi.endpoints.setUserProfile.matchFulfilled),
				(state, action) => {
					state.info.nick = action.meta.arg.originalArgs.data.nick;
				}
			)
			.addMatcher(isAnyOf(...registerMatchers), (state, action) => {
				state.info.roles = action.payload.roles;
				state.info.nick = action.payload.nick;
			})
			.addMatcher(
				isAnyOf(
					...loginMatchers,
					authApi.endpoints.authenticate.matchFulfilled
				),
				(state, action) => {
					if (action.meta) {
						const meta = action.meta.baseQueryMeta;

						// @ts-ignore
						const token: string = meta?.response?.headers?.get('X-token');// eslint-disable-line
						if (token) {
							TokenManager.saveToken(token);
						}
					}
					state.info.id = action.payload.id;
					state.info.isSafe = action.payload.isSafe;
					state.info.email = action.payload.email;
					state.info.nick = action.payload.nick;
					state.info.registrationDate = action.payload.registrationDate;
					state.info.roles = action.payload.roles ?? [];
					state.info.isJoiAi = !!action.payload.isJoiAi;
				}
			)
			.addMatcher(
				authApi.endpoints.updateSafeMode.matchFulfilled,
				(state, action) => {
					state.info.isSafe = action.meta.arg.originalArgs.isSafeMode;
				}
			);
	},
});
const userAuthInfo = (state: RootState) => state.auth.info;
const userRoles = (state: RootState) => state.auth.info.roles;
export const getUserId = createSelector(userAuthInfo, (info) => info.id);
export const getUserEmail = createSelector(userAuthInfo, (info) => info.email);
export const getUserNick = createSelector(userAuthInfo, (info) => info.nick);
export const getUserRegistrationDate = createSelector(
	userAuthInfo,
	(info) => new Date(info.registrationDate)
);

export const getIsDemoUser = createSelector(userRoles, (roles) =>
	roles.includes('demo')
);
export const getIsSafeMode = createSelector(
	userAuthInfo,
	(info) => info.isSafe
);
export const getIsCreator = createSelector(userRoles, (roles) =>
	roles.includes('digital.duplicate')
);
export const getIsFromJoiAI = createSelector(
	userAuthInfo,
	(info) => !!info.isJoiAi
);

export default authSlice.reducer;
